import { QueryParseError, QueryPosition } from './types'

export type QueryTokenType =
  | 'ident'
  | 'string'
  | 'number'
  | 'duration'
  | 'operator'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'eof'

export interface QueryToken {
  type: QueryTokenType
  /** Raw source text of the token (strings are unquoted/unescaped in `value`) */
  raw: string
  /** Normalized value: unescaped string content, ident text, operator symbol */
  value: string
  position: QueryPosition
}

const OPERATOR_CHARS = new Set(['=', '!', '>', '<', '~'])

/** -7d, 2w, 1M, 30m, 12h, 1y — a signed integer with a duration suffix */
const DURATION_RE = /^[+-]?\d+(m|h|d|w|M|y)$/

const isIdentStart = (ch: string) => /[A-Za-z_]/.test(ch)
const isIdentChar = (ch: string) => /[A-Za-z0-9_.]/.test(ch)
const isDigit = (ch: string) => ch >= '0' && ch <= '9'

export function tokenizeQuery(input: string): QueryToken[] {
  const tokens: QueryToken[] = []
  let i = 0

  const push = (
    type: QueryTokenType,
    start: number,
    end: number,
    value?: string,
  ) => {
    tokens.push({
      type,
      raw: input.slice(start, end),
      value: value ?? input.slice(start, end),
      position: { start, end },
    })
  }

  while (i < input.length) {
    const ch = input[i]

    if (/\s/.test(ch)) {
      i++
      continue
    }

    if (ch === '(') {
      push('lparen', i, i + 1)
      i++
      continue
    }
    if (ch === ')') {
      push('rparen', i, i + 1)
      i++
      continue
    }
    if (ch === ',') {
      push('comma', i, i + 1)
      i++
      continue
    }

    // Quoted strings: "..." or '...' with backslash escapes
    if (ch === '"' || ch === "'") {
      const quote = ch
      const start = i
      i++
      let value = ''
      let closed = false
      while (i < input.length) {
        const c = input[i]
        if (c === '\\' && i + 1 < input.length) {
          value += input[i + 1]
          i += 2
          continue
        }
        if (c === quote) {
          closed = true
          i++
          break
        }
        value += c
        i++
      }
      if (!closed) {
        throw new QueryParseError('Unterminated string', {
          start,
          end: input.length,
        })
      }
      push('string', start, i, value)
      continue
    }

    // Operators: != !~ >= <= = > < ~
    if (OPERATOR_CHARS.has(ch)) {
      const start = i
      const two = input.slice(i, i + 2)
      if (two === '!=' || two === '!~' || two === '>=' || two === '<=') {
        push('operator', start, start + 2)
        i += 2
        continue
      }
      if (ch === '!') {
        throw new QueryParseError(`Unexpected character '!'`, {
          start,
          end: start + 1,
        })
      }
      push('operator', start, start + 1)
      i++
      continue
    }

    // Numbers and durations (optionally signed: -7d, +2w, -3)
    if (isDigit(ch) || ((ch === '-' || ch === '+') && isDigit(input[i + 1]))) {
      const start = i
      i++ // consume sign or first digit
      while (i < input.length && /[0-9.]/.test(input[i])) i++
      // Duration suffix?
      if (i < input.length && /[mhdwMy]/.test(input[i])) {
        i++
        const raw = input.slice(start, i)
        if (!DURATION_RE.test(raw)) {
          throw new QueryParseError(`Invalid duration '${raw}'`, {
            start,
            end: i,
          })
        }
        push('duration', start, i)
        continue
      }
      const raw = input.slice(start, i)
      if (!/^[+-]?\d+(\.\d+)?$/.test(raw)) {
        throw new QueryParseError(`Invalid number '${raw}'`, { start, end: i })
      }
      push('number', start, i)
      continue
    }

    // Identifiers / keywords / bare-word values (dots allowed for cf.field)
    if (isIdentStart(ch)) {
      const start = i
      while (i < input.length && isIdentChar(input[i])) i++
      push('ident', start, i)
      continue
    }

    throw new QueryParseError(`Unexpected character '${ch}'`, {
      start: i,
      end: i + 1,
    })
  }

  push('eof', input.length, input.length, '')
  return tokens
}
